import React, { useMemo, useRef, useState, useEffect } from "react";
import api from "../../api";
import { motion, useAnimation } from "framer-motion";
import "./TrackLists.css";
import vinylImg from "../../assets/Disc/test.avif";

export default function TrackLists({ product }) {
  const sectionRef = useRef(null);
  const controls = useAnimation();

  const [trackListData, settrackListData] = useState([]);
  const [side, setSide] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  // const toPublicUrl = (p) => {
  //   if (!p) return "";
  //   const clean = p.replace(/^(\.\.\/)+/, "/");
  //   return api.defaults.baseURL.replace(/\/$/, "") + encodeURI(clean);
  // };
  const toPublicUrl = (p) => {
    // if (!p) return "";
    const value = (p || "").trim();

    if (!value || value.toLowerCase() === "n/a") {
      return "";
    }
    
    const clean = `/assets/images/${p}`; // prepend folder path
    return `${api.defaults.baseURL.replace(/\/$/, "")}${encodeURI(clean)}`;
  };

  const fetchTrackLists = async () => {
    if (!product?.id) return;

    try {
      const formData = new FormData();
      formData.append("Action", "getTracklist");
      formData.append("itemId", product.id);

      const res = await api.post("trackLists", formData);
      settrackListData(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchTrackLists();
  }, [product?.id]);

  const TracksFromDB = useMemo(() => {
    const grouped = {};

    for (const row of trackListData) {
      const sideKey = String(row.Side ?? "")
        .toUpperCase()
        .trim();
      if (!sideKey) continue;

      if (!grouped[sideKey]) grouped[sideKey] = [];

      grouped[sideKey].push({
        id: row.id,
        no: String(row.SongOrder ?? "").padStart(2, "0"),
        en: row.NameEng,
        ar: row.NameAr,
        dur: row.Duration ?? "",
        Disc: row.Disc,
        Side: row.Side,
        SongOrder: row.SongOrder,
      });
    }

    Object.keys(grouped).forEach((k) => {
      grouped[k].sort((a, b) => (a.SongOrder ?? 0) - (b.SongOrder ?? 0));
    });

    return grouped;
  }, [trackListData]);

  const sides = useMemo(() => Object.keys(TracksFromDB), [TracksFromDB]);

  useEffect(() => {
    if (sides.length && !sides.includes(side)) {
      setSide(sides[0]);
      setActiveIndex(0);
    }
  }, [sides, side]);

  const tracks = useMemo(() => TracksFromDB[side] ?? [], [TracksFromDB, side]);

  useEffect(() => {
    if (activeIndex > tracks.length - 1) {
      setActiveIndex(0);
    }
  }, [tracks, activeIndex]);

  const activeTrack = tracks[activeIndex] ?? tracks[0];

  useEffect(() => {
    controls.start({
      rotate: 360,
      transition: {
        repeat: Infinity,
        duration: 25,
        ease: "linear",
      },
    });

    const handleVisibilityChange = () => {
      if (document.hidden) {
        controls.stop();
      } else {
        controls.start({
          rotate: 360,
          transition: {
            repeat: Infinity,
            duration: 25,
            ease: "linear",
          },
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [controls]);


  return (
    <section className="tl-wrap" ref={sectionRef}>
      <div className="tl-bg" aria-hidden="true" />

      <div className="tl-grid">
        <div className="tl-left">
          <header className="tl-header">
            <h1 className="tl-title">TRACKLIST</h1>
            {/* <p className="tl-sub">
              A mix of soul and electronica for deep relaxation
            </p> */}

            <div className="tl-meta">
              <span className="tl-artist">{product?.artistEn}</span>
            </div>

            <nav className="tl-tabs" aria-label="Sides">
              {sides.map((s) => (
                <button
                  key={s}
                  className={`tl-tab ${side === s ? "is-active" : ""}`}
                  onClick={() => {
                    setSide(s);
                    setActiveIndex(0);
                  }}
                  type="button"
                >
                  SIDE {s}
                </button>
              ))}
            </nav>
          </header>

          <div className="tl-card">
            <div className="tl-now">
              <div className="tl-nowLeft">
                <div className="tl-nowText">
                  <div className="tl-nowTitle">{activeTrack?.en || "-"}</div>
                  <div className="tl-nowArtist">{product?.artistEn || "-"}</div>
                </div>
              </div>

              <div className="tl-nowDur">{activeTrack?.dur}</div>
            </div>

            <div className="tl-list" role="list">
              {tracks.map((t, idx) => {
                const isActive = idx === activeIndex;

                return (
                  <button
                    key={`${side}-${t.id}-${idx}`}
                    className={`tl-row ${isActive ? "is-active" : ""}`}
                    onClick={() => setActiveIndex(idx)}
                    type="button"
                    role="listitem"
                  >
                    <div className="tl-no">{t.no}</div>

                    <div className="tl-names">
                      <div className="tl-en">{t.en}</div>
                      {t.ar && <div className="tl-ar" dir="rtl">
                        {t.ar}
                      </div>}
                    </div>

                    <div className="tl-dur">{t.dur}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <aside className="tl-right" aria-hidden="true">
          <div className="tl-vinylSticky">
            <motion.img
              src={
                product?.disk_vinyl ? toPublicUrl(product.disk_vinyl) : vinylImg
              }
              alt="Vinyl Record"
              className="tl-vinyl"
              animate={controls}
              draggable={false}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = vinylImg;
              }}
            />
          </div>
        </aside>
      </div>
    </section>
  );
}
